import { Option } from 'src/pages/CaseDetails/interfaces/EmiSchedule.type';
import { ILocation } from '../LiveLocationTracker/constants/LiveLocationTrackerInterfaces';
import { ViewType, VISIT_PLAN_SOURCE } from './constants';
import { Address, LatestFeedback } from '../CaseDetails/components/TopAddresses/interfaces';
import { IFilterDrawerSchema } from '@cp/src/components/FilterDrawer/types';

export interface IAgent {
  referenceId: string;
  name: string;
  phoneNumber: string;
  location: ILocation;
  activityLevel: number;
  totalAllocatedCases: number;
  lastUpdatedAt: string | null;
  isTrainingAgent: boolean;
}

export interface AgentPinsResponse {
  pinsMetadata: IAgent[];
}

export interface ICasesThinList {
  caseReferenceId: string;
  addressId?: string;
  location: {
    latitude: number;
    longitude: number;
  };
  source?: VISIT_PLAN_SOURCE;
}

export interface VisitPlanState {
  // Filter data
  agencyFilterData: Array<Option>;
  teamLeadFilterData: Array<Option>;

  // Agents data
  agents: IAgent[];
  agentsLoading: boolean;
  activeTab: ViewType;

  // All cases data
  allCases: {
    data: ICasesData;
    thinList: ICasesThinList[];
  };
  casesLoading: boolean;
  visitPlanLoading: boolean;
  todayVisitPlanCases: {
    data: IVisitPlanData;
    thinList: ICasesThinList[];
  };
  previousVisitPlanCases: {
    data: IVisitPlanData;
    thinList: ICasesThinList[];
  };
  tomorrowVisitPlanCases: {
    data: IVisitPlanData;
    thinList: ICasesThinList[];
  };

  topAddresses: Address[];
  topAddressesLoading: boolean;

  selectedCaseId: string | null;
  caseFilterSchema: Record<string, IFilterDrawerSchema>;

  // Search functionality
  searchQuery: string;

  isDeleted: boolean;
}

export interface AgentMarkerProps {
  position: ILocation;
  agent: IAgent;
}

export interface TagIndicator {
  key: string;
  label: string;
  color: string;
  borderColor: string;
  boxShadow: string;
  fontColor: string;
}

// Case interaction status interface
export interface ICaseInteractionStatus {
  color: string;
  status: string;
  statusValue: string;
}

export interface AddressItem {
  addressId: string;
  rank: number;
  pinCode: string;
  addressText: string;
  city: string;
  location: {
    latitude: number;
    longitude: number;
  };
}

// Individual case data interface
export interface ICaseData {
  customerReferenceId: string;
  caseReferenceId: string;
  customerName: string;
  entityId: string;
  currentMonthCaseInteractionStatus?: ICaseInteractionStatus;
  lastMonthCaseInteractionStatus?: ICaseInteractionStatus;
  dpdBucket: string;
  paymentStatus: PaymentStatus;
  totalOverdueAmount: string;
  selfieUri: string;
  pinRank: number;
  address: AddressItem;
  businessVertical: string;
  visited: boolean | null;
}

export interface IVisitPlanCaseData {
  customerReferenceId: string;
  caseReferenceId: string;
  customerName: string;
  latestFeedback: LatestFeedback | null;
  dpdBucket: string;
  paymentStatus: PaymentStatus;
  totalOverdueAmount: string;
  visited: boolean | null;
  entityId: string;
  pinRank: number;
  address: AddressItem;
  selfieUri: string;
  businessVertical: string;
  localVisitPlan?: boolean;
  source?: VISIT_PLAN_SOURCE;
}

export interface IVisitPlanData {
  [key: string]: IVisitPlanCaseData;
}

// Cases dictionary interface (key-value pairs)
export interface ICasesData {
  [key: string]: ICaseData;
}

export enum PaymentStatus {
  PAID = 'PAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  UNPAID = 'UNPAID',
  CLOSED = 'CLOSED'
}

// Distance calculation interface
export interface ICaseItemLatLongData {
  latitude: number;
  longitude: number;
  id?: string;
}
